import React from "react";
import { FaStar } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/**
 * Real customer proof, placed high on the page — 74% of viewing time falls in
 * the first two screenfuls (NN/g), so proof that sits at the bottom is proof
 * most visitors never see.
 *
 * Deliberately reads the SAME i18n keys the home page uses
 * (home.testimonials.items.*) so there is exactly one copy of these quotes.
 * These are genuine Google reviews. Nothing here is invented: no stock
 * portraits, no fabricated counts, no "trusted by 500+ businesses".
 *
 * Note on faces: CXL's lab study found testimonials with a real photo are
 * significantly more memorable (p=0.0035). We show initials rather than a
 * photo because we do not have the reviewers' permission or images — a stock
 * face here would be a fabricated endorsement, which is exactly the thing that
 * destroys the trust the proof is meant to build.
 */
const ProofStrip = ({ reviewKeys = ["client1", "client2"], className = "" }) => {
  const { t } = useTranslation();

  const reviews = reviewKeys
    .map((key) => ({
      key,
      quote: t(`home.testimonials.items.${key}.quote`, ""),
      name: t(`home.testimonials.items.${key}.name`, ""),
      date: t(`home.testimonials.items.${key}.date`, ""),
    }))
    .filter((r) => r.quote && r.name);

  if (!reviews.length) return null;

  const initials = (name) =>
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase();

  return (
    <section className={`bg-moss-night border-y border-moss-deep/40 py-14 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
            <span className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-amber-400" aria-hidden="true" />
              ))}
            </span>
            <span className="text-sm text-silver-medium">
              {t("proof.googleRating", "5.0 on Google Reviews")}
            </span>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {reviews.map((r) => (
              <figure
                key={r.key}
                className="rounded-lg border border-moss-deep/50 bg-black/40 p-6"
              >
                <blockquote className="text-silver-medium leading-relaxed">
                  “{r.quote}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-moss-deep text-sm font-medium text-silver-light"
                  >
                    {initials(r.name)}
                  </span>
                  <span className="text-sm">
                    <span className="block font-medium text-silver-light">{r.name}</span>
                    {r.date && <span className="block text-silver-dark">{r.date}</span>}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProofStrip;
